import React from 'react'
import { useRouter } from '@src/routes'
import { Grid, FormControlLabel, Checkbox, Tooltip } from '@material-ui/core'
import { makeStyles, Theme } from '@material-ui/core/styles'
import Table from '@material-ui/core/Table'
import clsx from 'clsx'
import { configContext } from '../../../utils/StandardReducer'
import messages from '../../../messages'
import { FormattedMessage } from 'react-intl'
import { ConfiguratorProductDetails } from '../../ConfiguratorSharedTypes'
import { TubeProduct } from '../enum'
import InfoIcon from '@material-ui/icons/Info'
import globalStyles from '@src/routes/Configurators/utils/styles'

export interface SequenceProp {
  sequence: string
  baseCount: string
  molecularWeight: string
  secondaryStructure: string
  primerDimer: string
  meltingTemperature: string
  gcContent: string
}

const useStyles = makeStyles((theme: Theme) => {
  return {
    contentWrapper: {
      padding: theme.spacing(7, 5),
      marginTop: theme.spacing(-8),
    },
    subHead: {
      fontSize: theme.typography.pxToRem(16),
      fontWeight: theme.typography.fontWeightMedium,
      margin: theme.spacing(3, 0, 4),
    },

    label: {
      fontWeight: 'bold',
    },
    contentTable: {
      margin: theme.spacing(4, 0, 3),
      '& $contentRow': {
        textAlign: 'left',
      },
    },
    contentAquaTable: {
      margin: theme.spacing(4, 0, 3),
      width: theme.spacing(110),
      '& $contentAquaRow': {
        textAlign: 'left',
      },
    },
    lead: {
      width: theme.spacing(210),
      wordBreak: 'break-all',
    },
    pr20: {
      paddingRight: theme.spacing(5),
    },
    fontBold: {
      fontWeight: theme.typography.fontWeightMedium,
    },
    contentRow: {},
    contentAquaRow: {},
    overrideValidation: {
      textAlign: 'right',
      fontWeight: 'bold',
      paddingLeft: theme.spacing(3),
    },
    over1: {
      fontSize: theme.typography.pxToRem(14),
      color: '#1d5293',
      fontWeight: 'bold',
    },
    icon: {
      fontSize: theme.typography.pxToRem(16),
      verticalAlign: 'middle',
    },
    IconButton: {
      outlined: true,
      padding: theme.spacing(1),
      marginBottom: 2,
    },
    InfoPopup: {
      maxWidth: 270,
      backgroundColor: theme.palette.common.white,
      position: 'relative',
      boxShadow: theme.shadows[5],
      padding: theme.spacing(5),
      fontSize: theme.typography.pxToRem(12),
      color: theme.palette.common.black,
      borderRadius: '2%',
      marginTop: theme.spacing(1),
      marginBottom: theme.spacing(1),
    },
    arrow1: {
      color: theme.palette.common.white,
    },
    InfoIcon: {
      outlined: true,
      position: 'relative',
      fontSize: theme.typography.pxToRem(16),
      top: theme.spacing(0.5),
      marginLeft: theme.spacing(1.5),
    },
  }
})

interface SequencePropertiesProps {
  productDetails: ConfiguratorProductDetails
  sequencePreviewData: any
}

const SequenceProperties: React.FC<SequencePropertiesProps> = (props) => {
  const router = useRouter()
  const { product = '' } = router.query
  const classes = useStyles()
  const globalClasses = globalStyles()
  const { productDetails, sequencePreviewData } = props
  const { state, dispatch } = React.useContext(configContext)
  const { sessionData, currentOligoIndex } = state
  const [isOverride, setIsOverride] = React.useState<any>({
    checked: false,
  })

  React.useEffect(() => {
    if (
      state.isPageLoad === 2 &&
      sessionData !== null &&
      sessionData.length !== 0 &&
      sessionData[state.currentOligoIndex] !== null &&
      Object.keys(sessionData[state.currentOligoIndex]).length !== 0
    ) {
      if (
        sessionData[currentOligoIndex].isOverridden !== null &&
        sessionData[currentOligoIndex].isOverridden !== undefined
      ) {
        const data: any = sessionData[currentOligoIndex].isOverridden
        setIsOverride({
          ...isOverride,
          checked: data,
        })
      }
      dispatch({
        type: 'UPDATE_PAGE_LOAD',
        data: 4,
      })
    }
    // TODO: need a test before we refactor these dependencies
  }, [state.isPageLoad])

  const handleIsOverride = (event) => {
    setIsOverride({ ...isOverride, checked: event.target.checked })
    if (event.target.checked) {
      dispatch({
        type: 'UPDATE_VALID_SEQUENCE',
        data: true,
      })
    }
    if (
      sessionData[currentOligoIndex].sequence?.length === 0 &&
      !event.target.checked
    ) {
      dispatch({
        type: 'UPDATE_VALID_SEQUENCE',
        data: false,
      })
    }
    sessionData[currentOligoIndex].isOverridden = event.target.checked
    dispatch({
      type: 'CALL_OVERRIDE_VALIDATION',
      data: true,
    })
    dispatch({
      type: 'CALL_TOTAL_VALIDATION',
      data: true,
    })
    dispatch({
      type: 'UPDATE_ERROR_DATA',
      data: { ...state.errorData, isOverridden: event.target.checked },
    })

    dispatch({
      type: 'UPDATE_INPUT',
      data: [...state.sessionData],
    })
  }

  const aquaProperties = () => {
    return (
      <Table className={classes.contentAquaTable}>
        <thead>
          <tr>
            <th className={clsx(classes.contentAquaRow, classes.pr20)}>
              {' '}
              <FormattedMessage
                {...messages.CONF_CPEPTIDE_PROPERTIES_AMINO_ACIDS}
              />
            </th>
            <th className={classes.contentAquaRow}>
              <FormattedMessage {...messages.CONF_MOLECULAR_WEIGHT} />
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>{sequencePreviewData.baseCount}</td>
            <td>{sequencePreviewData.molecularWeight}</td>
          </tr>
        </tbody>
      </Table>
    )
  }

  const renderData = () => {
    return (
      <div className={classes.contentWrapper}>
        <h4 className={classes.subHead}>
          {productDetails.productType === TubeProduct.Cpeptide ||
          productDetails.productType === TubeProduct.Aqua ? (
            <FormattedMessage {...messages.CONF_CPEPTIDE_PROPERTIES} />
          ) : (
            <FormattedMessage {...messages.CONF_SEQUENCE_PROPERTIES} />
          )}
          <Tooltip
            title={
              <FormattedMessage
                {...messages.CONF_SEQUENCE_PROPERTIES_INFO_TEXT}
              />
            }
            classes={{ tooltip: classes.InfoPopup, arrow: classes.arrow1 }}
            arrow
            placement="bottom-end"
          >
            <InfoIcon
              aria-label="info"
              className={classes.InfoIcon}
              color="primary"
            ></InfoIcon>
          </Tooltip>
        </h4>

        <>
          <Grid container>
            <Grid item className={classes.fontBold}>
              <FormattedMessage {...messages.CONF_PREVIEW} />
            </Grid>
          </Grid>
          <Grid container>
            <Grid
              item
              className={clsx(classes.lead, globalClasses.globalHelpTextColor)}
            >
              {sequencePreviewData.sequence}
            </Grid>
          </Grid>
        </>

        {product === TubeProduct.Aqua ? (
          aquaProperties()
        ) : (
          <Table className={classes.contentTable}>
            <thead>
              <tr>
                <th className={clsx(classes.contentRow, classes.pr20)}>
                  {' '}
                  {productDetails.productType === TubeProduct.Cpeptide ? (
                    <FormattedMessage
                      {...messages.CONF_CPEPTIDE_PROPERTIES_AMINO_ACIDS}
                    />
                  ) : (
                    <FormattedMessage {...messages.CONF_BASES} />
                  )}
                </th>
                <th className={classes.contentRow}>
                  <FormattedMessage {...messages.CONF_MOLECULAR_WEIGHT} />
                </th>
                {productDetails.productType === TubeProduct.Cpeptide ? (
                  ''
                ) : (
                  <>
                    {' '}
                    <th className={classes.contentRow}>
                      <FormattedMessage
                        {...messages.CONF_SECONDARY_STRUCTURE}
                      />
                    </th>
                    <th className={classes.contentRow}>
                      <FormattedMessage {...messages.CONF_PRIMER_DIMER} />
                    </th>
                    <th className={classes.contentRow}>
                      <FormattedMessage {...messages.CONF_MELTING_TEMP} />
                    </th>
                    <th className={classes.contentRow}>
                      <FormattedMessage {...messages.CONF_GC_CONTENT} />
                    </th>
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{sequencePreviewData.baseCount}</td>
                <td>{sequencePreviewData.molecularWeight}</td>
                {productDetails.productType === TubeProduct.Cpeptide ? (
                  ''
                ) : (
                  <>
                    <td>{sequencePreviewData.secondaryStructure}</td>
                    <td>{sequencePreviewData.primerDimer}</td>
                    <td>{sequencePreviewData.meltingTemperature}</td>
                    <td>{sequencePreviewData.gcContent}</td>
                  </>
                )}
              </tr>
            </tbody>
          </Table>
        )}
        <Grid container>
          <Grid item sm={6}></Grid>

          {productDetails.isCSRSupervisor ? (
            <Grid item sm={6}>
              <Grid className={classes.overrideValidation}>
                <FormControlLabel
                  classes={{ label: classes.over1 }}
                  control={
                    <Checkbox
                      color="primary"
                      value={messages.CONF_OVERRIDE_VALIDATION.defaultMessage}
                    />
                  }
                  label={messages.CONF_OVERRIDE_VALIDATION.defaultMessage}
                  onChange={handleIsOverride}
                  checked={isOverride.checked}
                />
              </Grid>
            </Grid>
          ) : (
            ''
          )}
        </Grid>
      </div>
    )
  }
  return <>{renderData()}</>
}

export default SequenceProperties
